/**
 * AI Systems Inventory — Trust Center
 *
 * The killer differentiator: ArgusAI knows what AI systems are running on
 * its own platform, so it auto-populates this inventory. Every agent,
 * model, pipeline, tool, and fine-tuned adapter is registered with a full
 * Model Card (Google-style) and continuously monitored for faithfulness,
 * hallucination, injection attempts, and bias drift.
 *
 * Required by:
 *   - ISO/IEC 42001 (AI Management System) — AI inventory + oversight
 *   - NIST AI RMF — Govern + Map + Measure + Manage
 *   - FINOS AI Governance Framework v2
 *   - EU AI Act — high-risk system registration + post-market monitoring
 */

import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState, useMemo } from 'react';
import {
  Bot, Brain, Sparkles, Shield, AlertTriangle, CheckCircle2,
  Activity, TrendingUp, TrendingDown, Pause, Play, FileText,
  Search, Plus, X, ArrowRight, Eye, Gavel, BookOpen, Users,
  Zap, Lock, Target, History, FlaskConical, Network, ExternalLink,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from '@/components/ui/sheet';
import { SortableHeader, useTableSort } from '@/components/ui/data-table';
import { Separator } from '@/components/ui/separator';
import { RegisterAiSystemDialog } from '@/components/trust/register-ai-system-dialog';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import {
  type AiSystem,
  type AiSystemType,
  type AiRiskTier,
  type AiSystemStatus,
  type AiAutonomyLevel,
} from '@/lib/mock-trust-data';
import { useTrustAiSystems } from '@/lib/use-trust-ai-systems';

export const Route = createFileRoute('/trust/ai-systems')({
  component: AiSystemsInventoryPage,
});

// ── Helpers ─────────────────────────────────────────────────────────────────

function timeAgo(iso: string | null): string {
  if (!iso) return 'Never';
  const diff = Date.now() - new Date(iso).getTime();
  const days = Math.floor(diff / 86400000);
  if (days < 1) {
    const hrs = Math.floor(diff / 3600000);
    if (hrs < 1) return 'Just now';
    return `${hrs}h ago`;
  }
  if (days < 30) return `${days}d ago`;
  if (days < 365) return `${Math.floor(days / 30)}mo ago`;
  return `${Math.floor(days / 365)}y ago`;
}

function daysSince(iso: string | null): number {
  if (!iso) return Infinity;
  return Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
}

function formatPercent(n: number | null, decimals = 1): string {
  if (n === null || n === undefined) return '—';
  return `${(n * 100).toFixed(decimals)}%`;
}

// ── Style Tables ────────────────────────────────────────────────────────────

const TYPE_STYLES: Record<AiSystemType, { label: string; color: string; icon: typeof Bot }> = {
  agent:              { label: 'Agent',            color: 'bg-violet-500/10 text-violet-600 dark:text-violet-400 border-violet-500/20',    icon: Bot },
  model:              { label: 'Model',            color: 'bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500/20',            icon: Brain },
  pipeline:           { label: 'Pipeline',         color: 'bg-cyan-500/10 text-cyan-600 dark:text-cyan-400 border-cyan-500/20',            icon: Network },
  tool:               { label: 'Tool',             color: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20',icon: Zap },
  fine_tuned_adapter: { label: 'Fine-tuned',       color: 'bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20',        icon: Sparkles },
};

const STATUS_STYLES: Record<AiSystemStatus, { label: string; color: string }> = {
  in_development: { label: 'In Development', color: 'bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20' },
  in_review:      { label: 'In Review',      color: 'bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500/20' },
  in_production:  { label: 'Production',     color: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20' },
  deprecated:     { label: 'Deprecated',     color: 'bg-zinc-500/10 text-zinc-600 dark:text-zinc-400 border-zinc-500/20' },
  retired:        { label: 'Retired',        color: 'bg-red-500/10 text-red-600 dark:text-red-400 border-red-500/20' },
};

const RISK_TIER_STYLES: Record<AiRiskTier, { label: string; color: string; accent: string }> = {
  minimal:      { label: 'Minimal',      color: 'bg-zinc-500/10 text-zinc-600 dark:text-zinc-400 border-zinc-500/20',             accent: 'border-l-zinc-300 dark:border-l-zinc-700' },
  limited:      { label: 'Limited',      color: 'bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500/20',             accent: 'border-l-blue-400 dark:border-l-blue-600' },
  high:         { label: 'High',         color: 'bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20',         accent: 'border-l-amber-500 dark:border-l-amber-500' },
  unacceptable: { label: 'Unacceptable', color: 'bg-red-500/10 text-red-600 dark:text-red-400 border-red-500/20',                 accent: 'border-l-red-500 dark:border-l-red-500' },
};

const AUTONOMY_STYLES: Record<AiAutonomyLevel, { label: string; short: string; color: string; description: string }> = {
  human_in_loop:      { label: 'Human In Loop',      short: 'In Loop',  color: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20', description: 'Human approves every AI action before execution' },
  human_on_loop:      { label: 'Human On Loop',      short: 'On Loop',  color: 'bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500/20',             description: 'Human monitors; intervenes on exception' },
  human_out_of_loop:  { label: 'Human Out of Loop',  short: 'Out',      color: 'bg-red-500/10 text-red-600 dark:text-red-400 border-red-500/20',                 description: 'Autonomous. Requires enhanced monitoring + kill switch' },
};

const FRAMEWORK_LABELS: Record<string, string> = {
  iso42001:       'ISO 42001',
  nist_ai_rmf:    'NIST AI RMF',
  finos_ai_v2:    'FINOS AI v2',
  eu_ai_act:      'EU AI Act',
  cmmc_l2:        'CMMC L2',
  hipaa:          'HIPAA',
  gdpr:           'GDPR',
  itar:           'ITAR',
  iso27001:       'ISO 27001',
  soc2:           'SOC 2',
  nist80053:      'NIST 800-53',
};

const CLASSIFICATION_STYLES: Record<string, string> = {
  Unclassified: 'bg-zinc-500/10 text-zinc-600 dark:text-zinc-400 border-zinc-500/20',
  CUI:          'bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20',
  ITAR:         'bg-red-500/10 text-red-600 dark:text-red-400 border-red-500/20',
  PHI:          'bg-violet-500/10 text-violet-600 dark:text-violet-400 border-violet-500/20',
  Restricted:   'bg-orange-500/10 text-orange-600 dark:text-orange-400 border-orange-500/20',
};

// ── Scoring Helpers ─────────────────────────────────────────────────────────

function faithfulnessColor(score: number | null): string {
  if (score === null) return 'text-muted-foreground';
  if (score >= 0.9) return 'text-emerald-600 dark:text-emerald-400';
  if (score >= 0.8) return 'text-amber-600 dark:text-amber-400';
  return 'text-red-600 dark:text-red-400';
}

function hallucinationColor(rate: number | null): string {
  if (rate === null) return 'text-muted-foreground';
  if (rate < 0.01) return 'text-emerald-600 dark:text-emerald-400';
  if (rate < 0.02) return 'text-amber-600 dark:text-amber-400';
  return 'text-red-600 dark:text-red-400';
}

function biasTestColor(rate: number | null): string {
  if (rate === null) return 'text-muted-foreground';
  if (rate >= 0.95) return 'text-emerald-600 dark:text-emerald-400';
  if (rate >= 0.9) return 'text-amber-600 dark:text-amber-400';
  return 'text-red-600 dark:text-red-400';
}

function metricBelowThreshold(value: number | string, threshold: number | string | null): boolean {
  if (threshold === null) return false;
  // Numeric comparison
  if (typeof value === 'number' && typeof threshold === 'number') {
    return value < threshold;
  }
  // String-encoded percentages (e.g. "0.8%" vs "< 2%")
  if (typeof value === 'string' && typeof threshold === 'string') {
    const v = parseFloat(value.replace(/[^\d.-]/g, ''));
    const t = parseFloat(threshold.replace(/[^\d.-]/g, ''));
    if (Number.isNaN(v) || Number.isNaN(t)) return false;
    if (threshold.includes('<')) return v >= t;
    if (threshold.includes('>')) return v <= t;
    return v < t;
  }
  return false;
}

// ── Page ────────────────────────────────────────────────────────────────────

function AiSystemsInventoryPage() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState<'all' | AiSystemType>('all');
  const [statusFilter, setStatusFilter] = useState<'all' | AiSystemStatus>('all');
  const [riskFilter, setRiskFilter] = useState<'all' | AiRiskTier>('all');
  const [autonomyFilter, setAutonomyFilter] = useState<'all' | AiAutonomyLevel>('all');
  const [selectedSystem, setSelectedSystem] = useState<AiSystem | null>(null);
  const [registerOpen, setRegisterOpen] = useState(false);

  // Real-data: A4 ai_systems_inventory → AiSystem VM via adapter.
  const { data: AI_SYSTEMS = [] } = useTrustAiSystems();

  // ── Aggregate Stats ───────────────────────────────────────────────────────
  const stats = useMemo(() => {
    const total = AI_SYSTEMS.length;
    const inProd = AI_SYSTEMS.filter(s => s.status === 'in_production').length;
    const systemsWithFaithfulness = AI_SYSTEMS.filter(s => s.faithfulnessScore !== null);
    const avgFaith = systemsWithFaithfulness.length > 0
      ? systemsWithFaithfulness.reduce((sum, s) => sum + (s.faithfulnessScore ?? 0), 0) / systemsWithFaithfulness.length
      : 0;
    const systemsWithHalluc = AI_SYSTEMS.filter(s => s.hallucinationRate30d !== null);
    const avgHalluc = systemsWithHalluc.length > 0
      ? systemsWithHalluc.reduce((sum, s) => sum + (s.hallucinationRate30d ?? 0), 0) / systemsWithHalluc.length
      : 0;
    return { total, inProd, avgFaith, avgHalluc };
  }, [AI_SYSTEMS]);

  // ── Filter ────────────────────────────────────────────────────────────────
  const filtered = useMemo(() => {
    return AI_SYSTEMS.filter(sys => {
      if (typeFilter !== 'all' && sys.type !== typeFilter) return false;
      if (statusFilter !== 'all' && sys.status !== statusFilter) return false;
      if (riskFilter !== 'all' && sys.riskTier !== riskFilter) return false;
      if (autonomyFilter !== 'all' && sys.autonomyLevel !== autonomyFilter) return false;
      if (!search) return true;
      const q = search.toLowerCase();
      return (
        sys.name.toLowerCase().includes(q) ||
        sys.description.toLowerCase().includes(q) ||
        sys.intendedPurpose.toLowerCase().includes(q) ||
        sys.ownerName.toLowerCase().includes(q)
      );
    });
  }, [AI_SYSTEMS, search, typeFilter, statusFilter, riskFilter, autonomyFilter]);

  const { sorted, sortConfig, handleSort } = useTableSort(filtered, 'name');

  // ── Actions ───────────────────────────────────────────────────────────────
  const handleAddSystem = () => {
    // Trust bug #11: open the real registration dialog (was a theatrical toast
    // that opened nothing). The dialog persists via POST /governance/ai-systems.
    setRegisterOpen(true);
  };

  const handleRunBiasTest = (sys: AiSystem) => {
    // Platform audit 2026-06-12: NO backend runs bias tests — don't claim one was
    // queued on a compliance surface (was a fake toast.success).
    toast.info(`Bias testing not yet available for ${sys.name}`, {
      description: 'Automated bias/fairness evaluation is not wired to a backend yet.',
    });
    setSelectedSystem(null);
  };

  const handlePauseAgent = (sys: AiSystem) => {
    if (sys.linkedAgentId) {
      navigate({ to: '/agents/$agentId', params: { agentId: sys.linkedAgentId } });
      return;
    }
    toast.warning(`${sys.name} paused`, {
      description: sys.stopButtonProcedure,
    });
    setSelectedSystem(null);
  };

  const handleViewAudit = (sys: AiSystem) => {
    navigate({ to: '/trust/audit', search: { q: sys.name } as any });
    toast.success('Opening audit log', { description: `Filtered by ${sys.name}.` });
    setSelectedSystem(null);
  };

  const handleGenerateReport = (sys: AiSystem) => {
    // Platform audit 2026-06-12: this is a CLIENT-SIDE export of the system's
    // currently-displayed metrics — NOT a backend-generated, framework-assessed
    // compliance report. The old toast falsely claimed "Compliance report
    // generated... Covers ISO 42001, NIST AI RMF, FINOS AI v2" on a compliance
    // surface. Honest framing instead.
    toast.success(`Exported ${sys.name} metrics summary`, {
      description: 'A plain-text snapshot of this system\'s displayed metrics — not an official multi-framework compliance assessment.',
    });
    const body = `AI System Metrics Summary (client-side export — not an official compliance assessment)
================================

System: ${sys.name}
Type: ${sys.type}
Status: ${sys.status}
Risk Tier: ${sys.riskTier}
Autonomy: ${sys.autonomyLevel}
Owner: ${sys.ownerName} (${sys.ownerTeam})

Base Model: ${sys.baseModel}
${sys.fineTuningAdapterId ? `Fine-Tuning Adapter: ${sys.fineTuningAdapterId}` : 'Fine-Tuning Adapter: (none)'}

Hallucination Rate (30d): ${((sys.hallucinationRate30d ?? 0) * 100).toFixed(2)}%
Faithfulness Score: ${((sys.faithfulnessScore ?? 0) * 100).toFixed(1)}%
Injection Attempts Blocked: ${sys.injectionsBlocked30d}/${sys.injectionAttempts30d}
Bias Test Pass Rate: ${((sys.biasTestPassRate ?? 0) * 100).toFixed(1)}%
Last Bias Test: ${sys.lastBiasTestDate ? new Date(sys.lastBiasTestDate).toLocaleDateString() : 'Never'}

Framework Relevance: ${sys.frameworkRelevance.join(', ')}
Classifications Handled: ${sys.classificationsHandled.join(', ')}

Model Card: ${sys.modelCard ? '(attached)' : '(not yet generated)'}

Stop Button Procedure:
${sys.stopButtonProcedure}
`;
    // Use dynamic import so we don't blow up the bundle if trust-downloads isn't loaded yet
    import('@/lib/trust-downloads').then(({ triggerDownload, generateMockPdfContent }) => {
      triggerDownload({
        filename: `ai-system-${sys.id}-compliance-report.txt`,
        content: generateMockPdfContent(`${sys.name} — Compliance Report`, body),
        mimeType: 'text/plain',
      });
    });
  };

  return (
    <div className="p-6 space-y-6">
      {/* ── Header ──────────────────────────────────────────────────────── */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold flex items-center gap-2">
            <Bot className="h-5 w-5 text-primary" />
            AI Systems Inventory
          </h1>
          <p className="text-xs text-muted-foreground mt-1 max-w-2xl">
            Every AI system on this platform is registered, model-carded, and continuously monitored.
            Required by ISO/IEC 42001, NIST AI RMF, and FINOS AI Governance v2.
          </p>
        </div>
        <Button size="sm" className="gap-1.5" onClick={handleAddSystem}>
          <Plus className="h-3.5 w-3.5" /> Register AI System
        </Button>
      </div>

      {/* ── Stats ───────────────────────────────────────────────────────── */}
      <div className="grid grid-cols-4 gap-3">
        <Card>
          <CardContent className="pt-4 pb-3 text-center">
            <p className="text-2xl font-bold">{stats.total}</p>
            <p className="text-xs text-muted-foreground">Total AI Systems</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-3 text-center">
            <p className="text-2xl font-bold text-emerald-600 dark:text-emerald-400">{stats.inProd}</p>
            <p className="text-xs text-muted-foreground">In Production</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-3 text-center">
            <p className={cn('text-2xl font-bold', faithfulnessColor(stats.avgFaith))}>
              {formatPercent(stats.avgFaith, 1)}
            </p>
            <p className="text-xs text-muted-foreground">Avg Faithfulness</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-3 text-center">
            <p className={cn('text-2xl font-bold', hallucinationColor(stats.avgHalluc))}>
              {formatPercent(stats.avgHalluc, 2)}
            </p>
            <p className="text-xs text-muted-foreground">Avg Hallucination Rate (30d)</p>
          </CardContent>
        </Card>
      </div>

      {/* ── Filters ─────────────────────────────────────────────────────── */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search by name, purpose, or owner..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>
        <Select value={typeFilter} onValueChange={v => setTypeFilter(v as typeof typeFilter)}>
          <SelectTrigger className="w-32 h-9 text-xs"><SelectValue placeholder="Type" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            <SelectItem value="agent">Agent</SelectItem>
            <SelectItem value="model">Model</SelectItem>
            <SelectItem value="pipeline">Pipeline</SelectItem>
            <SelectItem value="tool">Tool</SelectItem>
            <SelectItem value="fine_tuned_adapter">Fine-tuned Adapter</SelectItem>
          </SelectContent>
        </Select>
        <Select value={statusFilter} onValueChange={v => setStatusFilter(v as typeof statusFilter)}>
          <SelectTrigger className="w-32 h-9 text-xs"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="in_production">Production</SelectItem>
            <SelectItem value="in_review">In Review</SelectItem>
            <SelectItem value="in_development">In Development</SelectItem>
            <SelectItem value="deprecated">Deprecated</SelectItem>
            <SelectItem value="retired">Retired</SelectItem>
          </SelectContent>
        </Select>
        <Select value={riskFilter} onValueChange={v => setRiskFilter(v as typeof riskFilter)}>
          <SelectTrigger className="w-28 h-9 text-xs"><SelectValue placeholder="Risk" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Risk</SelectItem>
            <SelectItem value="minimal">Minimal</SelectItem>
            <SelectItem value="limited">Limited</SelectItem>
            <SelectItem value="high">High</SelectItem>
            <SelectItem value="unacceptable">Unacceptable</SelectItem>
          </SelectContent>
        </Select>
        <Select value={autonomyFilter} onValueChange={v => setAutonomyFilter(v as typeof autonomyFilter)}>
          <SelectTrigger className="w-36 h-9 text-xs"><SelectValue placeholder="Autonomy" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Autonomy</SelectItem>
            <SelectItem value="human_in_loop">Human In Loop</SelectItem>
            <SelectItem value="human_on_loop">Human On Loop</SelectItem>
            <SelectItem value="human_out_of_loop">Human Out of Loop</SelectItem>
          </SelectContent>
        </Select>
        <span className="text-xs text-muted-foreground ml-auto">
          {filtered.length} system{filtered.length !== 1 ? 's' : ''}
        </span>
      </div>

      {/* ── Table ───────────────────────────────────────────────────────── */}
      {sorted.length > 0 ? (
        <div className="rounded-lg border overflow-x-auto">
          <div className="min-w-[1400px]">
            <div className="grid grid-cols-[minmax(320px,2fr)_110px_110px_100px_110px_140px_100px_110px_110px_100px_110px] gap-3 px-4 py-3 bg-muted/50 text-xs uppercase tracking-wider">
              <SortableHeader label="AI System"        sortKey="name"                 currentSort={sortConfig} onSort={handleSort} />
              <SortableHeader label="Type"             sortKey="type"                 currentSort={sortConfig} onSort={handleSort} />
              <SortableHeader label="Status"           sortKey="status"               currentSort={sortConfig} onSort={handleSort} />
              <SortableHeader label="Risk Tier"        sortKey="riskTier"             currentSort={sortConfig} onSort={handleSort} />
              <SortableHeader label="Autonomy"         sortKey="autonomyLevel"        currentSort={sortConfig} onSort={handleSort} />
              <SortableHeader label="Owner"            sortKey="ownerName"            currentSort={sortConfig} onSort={handleSort} />
              <SortableHeader label="Faithfulness"     sortKey="faithfulnessScore"    currentSort={sortConfig} onSort={handleSort} />
              <SortableHeader label="Hallucination"    sortKey="hallucinationRate30d" currentSort={sortConfig} onSort={handleSort} />
              <SortableHeader label="Injection Block"  sortKey="injectionsBlocked30d" currentSort={sortConfig} onSort={handleSort} />
              <SortableHeader label="Bias Pass"        sortKey="biasTestPassRate"     currentSort={sortConfig} onSort={handleSort} />
              <SortableHeader label="Last Bias Test"   sortKey="lastBiasTestDate"     currentSort={sortConfig} onSort={handleSort} />
            </div>
            <div className="divide-y">
              {sorted.map(sys => {
                const typeStyle = TYPE_STYLES[sys.type];
                const statusStyle = STATUS_STYLES[sys.status];
                const riskStyle = RISK_TIER_STYLES[sys.riskTier];
                const autStyle = AUTONOMY_STYLES[sys.autonomyLevel];
                const TypeIcon = typeStyle.icon;

                const injectionRate = sys.injectionAttempts30d > 0
                  ? (sys.injectionsBlocked30d / sys.injectionAttempts30d)
                  : 1;
                const injectionOk = injectionRate >= 0.99;

                const biasDays = daysSince(sys.lastBiasTestDate);
                const biasStale = biasDays > 90;

                return (
                  <div
                    key={sys.id}
                    className={cn(
                      'grid grid-cols-[minmax(320px,2fr)_110px_110px_100px_110px_140px_100px_110px_110px_100px_110px] gap-3 px-4 py-3 items-center hover:bg-muted/30 transition-colors cursor-pointer group border-l-4',
                      riskStyle.accent,
                      (sys.status === 'retired' || sys.status === 'deprecated') && 'opacity-60',
                    )}
                    onClick={() => setSelectedSystem(sys)}
                  >
                    {/* Name + description */}
                    <div className="flex items-start gap-3 min-w-0">
                      <div className={cn(
                        'flex h-8 w-8 shrink-0 items-center justify-center rounded-md',
                        typeStyle.color,
                      )}>
                        <TypeIcon className="h-4 w-4" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-semibold truncate">{sys.name}</p>
                        <p className="text-xs text-muted-foreground truncate">{sys.description}</p>
                        <div className="flex items-center gap-2 mt-0.5 text-[11px] text-muted-foreground/70">
                          <span className="font-mono">{sys.baseModel}</span>
                          {sys.fineTuningAdapterId && (
                            <span className="inline-flex items-center gap-0.5">
                              <Sparkles className="h-2.5 w-2.5" />
                              LoRA
                            </span>
                          )}
                          {sys.monitoringEnabled && (
                            <span className="inline-flex items-center gap-0.5 text-emerald-600 dark:text-emerald-400">
                              <Activity className="h-2.5 w-2.5" />
                              Live
                            </span>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Type */}
                    <Badge variant="outline" className={cn('text-[10px] w-fit', typeStyle.color)}>
                      {typeStyle.label}
                    </Badge>

                    {/* Status */}
                    <Badge variant="outline" className={cn('text-[10px] w-fit', statusStyle.color)}>
                      {statusStyle.label}
                    </Badge>

                    {/* Risk Tier */}
                    <Badge variant="outline" className={cn('text-[10px] w-fit', riskStyle.color)}>
                      {riskStyle.label}
                    </Badge>

                    {/* Autonomy */}
                    <Badge
                      variant="outline"
                      className={cn('text-[10px] w-fit', autStyle.color)}
                      title={autStyle.description}
                    >
                      {autStyle.short}
                    </Badge>

                    {/* Owner */}
                    <div className="min-w-0">
                      <p className="text-xs font-medium truncate">{sys.ownerName}</p>
                      <p className="text-[11px] text-muted-foreground truncate">{sys.ownerTeam}</p>
                    </div>

                    {/* Faithfulness */}
                    {sys.faithfulnessScore !== null ? (
                      <span className={cn('text-sm font-semibold tabular-nums', faithfulnessColor(sys.faithfulnessScore))}>
                        {formatPercent(sys.faithfulnessScore, 1)}
                      </span>
                    ) : (
                      <span className="text-sm text-muted-foreground">—</span>
                    )}

                    {/* Hallucination */}
                    {sys.hallucinationRate30d !== null ? (
                      <span className={cn('text-sm font-semibold tabular-nums', hallucinationColor(sys.hallucinationRate30d))}>
                        {formatPercent(sys.hallucinationRate30d, 2)}
                      </span>
                    ) : (
                      <span className="text-sm text-muted-foreground">—</span>
                    )}

                    {/* Injection Block Rate */}
                    <div className="text-xs">
                      <span className={cn('font-semibold tabular-nums', injectionOk ? 'text-emerald-600 dark:text-emerald-400' : 'text-amber-600 dark:text-amber-400')}>
                        {sys.injectionsBlocked30d}/{sys.injectionAttempts30d}
                      </span>
                      {sys.injectionAttempts30d > 0 && (
                        <p className="text-[10px] text-muted-foreground/70 tabular-nums">
                          {formatPercent(injectionRate, 1)}
                        </p>
                      )}
                    </div>

                    {/* Bias Pass Rate */}
                    {sys.biasTestPassRate !== null ? (
                      <span className={cn('text-sm font-semibold tabular-nums', biasTestColor(sys.biasTestPassRate))}>
                        {formatPercent(sys.biasTestPassRate, 0)}
                      </span>
                    ) : (
                      <span className="text-sm text-muted-foreground">—</span>
                    )}

                    {/* Last Bias Test */}
                    <span className={cn(
                      'text-xs',
                      biasStale ? 'text-red-600 dark:text-red-400 font-semibold' : 'text-muted-foreground',
                    )}>
                      {timeAgo(sys.lastBiasTestDate)}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center py-20 text-center">
          <Bot className="h-10 w-10 text-muted-foreground/20 mb-3" />
          <p className="text-sm font-medium">No AI systems found</p>
          <p className="text-xs text-muted-foreground mt-1">
            {search || typeFilter !== 'all' || statusFilter !== 'all' || riskFilter !== 'all' || autonomyFilter !== 'all'
              ? 'Try adjusting your filters'
              : 'Register an AI system to begin compliance tracking'}
          </p>
          <Button size="sm" className="mt-4 gap-1.5" onClick={handleAddSystem}>
            <Plus className="h-3.5 w-3.5" /> Register AI System
          </Button>
        </div>
      )}

      {/* ── Model Card Sheet ────────────────────────────────────────────── */}
      <Sheet open={!!selectedSystem} onOpenChange={(open) => !open && setSelectedSystem(null)}>
        <SheetContent
          side="right"
          className="w-full sm:max-w-[800px] overflow-y-auto p-0"
        >
          {selectedSystem && (
            <ModelCardPanel
              system={selectedSystem}
              allSystems={AI_SYSTEMS}
              onRunBiasTest={handleRunBiasTest}
              onPauseAgent={handlePauseAgent}
              onViewAudit={handleViewAudit}
              onGenerateReport={handleGenerateReport}
              onOpenInAgents={(sys) => {
                if (sys.linkedAgentId) {
                  setSelectedSystem(null);
                  navigate({ to: '/agents/$agentId', params: { agentId: sys.linkedAgentId } });
                }
              }}
              onClose={() => setSelectedSystem(null)}
            />
          )}
        </SheetContent>
      </Sheet>

      <RegisterAiSystemDialog open={registerOpen} onOpenChange={setRegisterOpen} />
    </div>
  );
}

// ── Model Card Panel ────────────────────────────────────────────────────────

interface ModelCardPanelProps {
  system: AiSystem;
  allSystems: AiSystem[];
  onRunBiasTest: (sys: AiSystem) => void;
  onPauseAgent: (sys: AiSystem) => void;
  onViewAudit: (sys: AiSystem) => void;
  onGenerateReport: (sys: AiSystem) => void;
  onOpenInAgents: (sys: AiSystem) => void;
  onClose: () => void;
}

function ModelCardPanel({
  system, allSystems, onRunBiasTest, onPauseAgent, onViewAudit, onGenerateReport, onOpenInAgents, onClose,
}: ModelCardPanelProps) {
  const AI_SYSTEMS = allSystems;
  const typeStyle = TYPE_STYLES[system.type];
  const statusStyle = STATUS_STYLES[system.status];
  const riskStyle = RISK_TIER_STYLES[system.riskTier];
  const autStyle = AUTONOMY_STYLES[system.autonomyLevel];
  const TypeIcon = typeStyle.icon;

  const injectionRate = system.injectionAttempts30d > 0
    ? (system.injectionsBlocked30d / system.injectionAttempts30d)
    : 1;

  const card = system.modelCard;

  return (
    <>
      {/* Sticky header */}
      <SheetHeader className="sticky top-0 z-10 border-b bg-popover px-6 py-5">
        <div className="flex items-start gap-4">
          <div className={cn(
            'flex h-12 w-12 shrink-0 items-center justify-center rounded-lg',
            typeStyle.color,
          )}>
            <TypeIcon className="h-6 w-6" />
          </div>
          <div className="min-w-0 flex-1">
            <SheetTitle className="text-lg leading-tight">{system.name}</SheetTitle>
            <SheetDescription className="mt-1 text-xs leading-relaxed">
              {system.description}
            </SheetDescription>
            <div className="flex flex-wrap items-center gap-1.5 mt-3">
              <Badge variant="outline" className={cn('text-[10px]', statusStyle.color)}>
                {statusStyle.label}
              </Badge>
              <Badge variant="outline" className={cn('text-[10px]', riskStyle.color)}>
                <Shield className="mr-1 h-2.5 w-2.5" />
                {riskStyle.label} Risk
              </Badge>
              <Badge variant="outline" className={cn('text-[10px]', autStyle.color)}>
                <Users className="mr-1 h-2.5 w-2.5" />
                {autStyle.label}
              </Badge>
              <Badge variant="outline" className={cn('text-[10px]', typeStyle.color)}>
                {typeStyle.label}
              </Badge>
            </div>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose} className="h-7 w-7 shrink-0" aria-label="Close" title="Close">
            <X className="h-4 w-4" />
          </Button>
        </div>
        <div className="mt-3 flex items-center gap-3 text-xs text-muted-foreground">
          <span className="inline-flex items-center gap-1">
            <Users className="h-3 w-3" /> {system.ownerName} · {system.ownerTeam}
          </span>
          <span className="text-border">|</span>
          <span className="font-mono">{system.baseModel}</span>
          {system.fineTuningAdapterId && (
            <>
              <span className="text-border">|</span>
              <span className="inline-flex items-center gap-1 text-amber-600 dark:text-amber-400">
                <Sparkles className="h-3 w-3" />
                {system.fineTuningAdapterId}
              </span>
            </>
          )}
        </div>
      </SheetHeader>

      {/* Body */}
      <div className="px-6 py-6 space-y-6">
        {/* Intended Purpose quote */}
        <section>
          <SectionTitle icon={Target}>Intended Purpose</SectionTitle>
          <p className="text-sm leading-relaxed italic text-muted-foreground border-l-2 border-primary/50 pl-3">
            "{system.intendedPurpose}"
          </p>
        </section>

        {card && (
          <>
            <Separator />

            {/* Model Description */}
            <section>
              <SectionTitle icon={BookOpen}>Model Description</SectionTitle>
              <p className="text-sm leading-relaxed">{card.modelDescription}</p>
            </section>

            {/* Intended Uses */}
            <section>
              <SectionTitle icon={CheckCircle2}>Intended Uses</SectionTitle>
              <ul className="space-y-1.5">
                {card.intendedUses.map((use, i) => (
                  <li key={i} className="text-sm flex gap-2">
                    <CheckCircle2 className="h-3.5 w-3.5 mt-0.5 shrink-0 text-emerald-600 dark:text-emerald-400" />
                    <span>{use}</span>
                  </li>
                ))}
              </ul>
            </section>

            {/* Out-of-Scope Uses */}
            <section>
              <SectionTitle icon={AlertTriangle} accent="red">Out-of-Scope Uses</SectionTitle>
              <ul className="space-y-1.5">
                {card.outOfScopeUses.map((use, i) => (
                  <li key={i} className="text-sm flex gap-2">
                    <X className="h-3.5 w-3.5 mt-0.5 shrink-0 text-red-600 dark:text-red-400" />
                    <span>{use}</span>
                  </li>
                ))}
              </ul>
            </section>

            {/* Performance Factors */}
            <section>
              <SectionTitle icon={Activity}>Performance Factors</SectionTitle>
              <ul className="space-y-1.5">
                {card.performanceFactors.map((factor, i) => (
                  <li key={i} className="text-sm flex gap-2">
                    <span className="text-muted-foreground mt-0.5">•</span>
                    <span className="text-muted-foreground">{factor}</span>
                  </li>
                ))}
              </ul>
            </section>

            {/* Metrics */}
            <section>
              <SectionTitle icon={TrendingUp}>Metrics &amp; Thresholds</SectionTitle>
              <div className="rounded-lg border overflow-hidden">
                <div className="grid grid-cols-[1fr_90px_100px] gap-2 bg-muted/50 px-3 py-2 text-[11px] uppercase tracking-wider text-muted-foreground">
                  <span>Metric</span>
                  <span className="text-right">Value</span>
                  <span className="text-right">Threshold</span>
                </div>
                <div className="divide-y">
                  {card.metrics.map((m, i) => {
                    const below = metricBelowThreshold(m.value, m.threshold);
                    return (
                      <div key={i} className="grid grid-cols-[1fr_90px_100px] gap-2 px-3 py-2 text-xs items-center">
                        <span className="font-medium">{m.name}</span>
                        <span className={cn(
                          'text-right font-semibold tabular-nums',
                          below ? 'text-red-600 dark:text-red-400' : 'text-emerald-600 dark:text-emerald-400',
                        )}>
                          {m.value}
                        </span>
                        <span className="text-right text-muted-foreground tabular-nums">
                          {m.threshold ?? '—'}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </section>

            {/* Ethical Considerations */}
            <section>
              <SectionTitle icon={Gavel}>Ethical Considerations</SectionTitle>
              <ul className="space-y-1.5">
                {card.ethicalConsiderations.map((eth, i) => (
                  <li key={i} className="text-sm flex gap-2">
                    <span className="text-amber-600 dark:text-amber-400 mt-0.5">▸</span>
                    <span className="text-muted-foreground">{eth}</span>
                  </li>
                ))}
              </ul>
            </section>

            {/* Caveats */}
            <section>
              <SectionTitle icon={AlertTriangle}>Caveats &amp; Limitations</SectionTitle>
              <ul className="space-y-1.5">
                {card.caveats.map((cav, i) => (
                  <li key={i} className="text-sm flex gap-2">
                    <span className="text-muted-foreground mt-0.5">•</span>
                    <span className="text-muted-foreground">{cav}</span>
                  </li>
                ))}
              </ul>
            </section>

            {/* Training Data */}
            <section>
              <SectionTitle icon={FlaskConical}>Training Data</SectionTitle>
              <p className="text-sm leading-relaxed mb-3">{card.trainingData}</p>
              {card.trainingDataLineage.length > 0 && (
                <div className="rounded-md bg-muted/40 p-3 space-y-1">
                  <p className="text-[11px] uppercase tracking-wider text-muted-foreground mb-1.5">Lineage</p>
                  {card.trainingDataLineage.map((line, i) => (
                    <p key={i} className="text-xs font-mono text-muted-foreground">
                      {line}
                    </p>
                  ))}
                </div>
              )}
            </section>

            {/* Quantitative Analyses */}
            {card.quantitativeAnalyses.length > 0 && (
              <section>
                <SectionTitle icon={TrendingUp}>Quantitative Analyses (Per-Group)</SectionTitle>
                <ul className="space-y-1.5">
                  {card.quantitativeAnalyses.map((qa, i) => (
                    <li key={i} className="text-sm flex gap-2">
                      <span className="text-muted-foreground mt-0.5">◆</span>
                      <span>{qa}</span>
                    </li>
                  ))}
                </ul>
              </section>
            )}
          </>
        )}

        <Separator />

        {/* Tool Grants */}
        {system.toolGrants.length > 0 && (
          <section>
            <SectionTitle icon={Zap}>Tool Grants ({system.toolGrants.length})</SectionTitle>
            <div className="flex flex-wrap gap-1.5">
              {system.toolGrants.map((tool) => (
                <Badge key={tool} variant="outline" className="text-[10px] font-mono">
                  {tool}
                </Badge>
              ))}
            </div>
          </section>
        )}

        {/* Knowledge Folders */}
        {system.knowledgeFolders.length > 0 && (
          <section>
            <SectionTitle icon={BookOpen}>Knowledge Folders ({system.knowledgeFolders.length})</SectionTitle>
            <div className="flex flex-wrap gap-1.5">
              {system.knowledgeFolders.map((folder) => (
                <Badge key={folder} variant="outline" className="text-[10px] bg-blue-500/5 text-blue-700 dark:text-blue-400 border-blue-500/20">
                  {folder}
                </Badge>
              ))}
            </div>
          </section>
        )}

        {/* Data Flows */}
        <section className="grid grid-cols-2 gap-4">
          <div>
            <SectionTitle icon={ArrowRight}>Data Inputs</SectionTitle>
            <div className="space-y-1">
              {system.dataInputs.map((inp, i) => (
                <p key={i} className="text-xs text-muted-foreground">• {inp}</p>
              ))}
            </div>
          </div>
          <div>
            <SectionTitle icon={ArrowRight}>Data Outputs</SectionTitle>
            <div className="space-y-1">
              {system.dataOutputs.map((out, i) => (
                <p key={i} className="text-xs text-muted-foreground">• {out}</p>
              ))}
            </div>
          </div>
        </section>

        {/* Approval Gates */}
        <section className="grid grid-cols-2 gap-3">
          <div className="rounded-lg border p-3">
            <p className="text-[11px] uppercase tracking-wider text-muted-foreground">Approval Gates</p>
            <div className="flex items-baseline gap-1 mt-1">
              <span className="text-2xl font-bold">{system.approvalGatesCount}</span>
              <span className="text-xs text-muted-foreground">gates</span>
            </div>
            <p className="text-[11px] text-muted-foreground mt-1">
              Human approvals required before execution
            </p>
          </div>
          <div className="rounded-lg border p-3">
            <p className="text-[11px] uppercase tracking-wider text-muted-foreground">Retraining Cadence</p>
            <p className="text-lg font-semibold capitalize mt-1">{system.retrainingCadence}</p>
            <p className="text-[11px] text-muted-foreground mt-1">
              Fine-tuning refresh schedule
            </p>
          </div>
        </section>

        {/* Classifications */}
        {system.classificationsHandled.length > 0 && (
          <section>
            <SectionTitle icon={Lock}>Classifications Handled</SectionTitle>
            <div className="flex flex-wrap gap-1.5">
              {system.classificationsHandled.map((cls) => (
                <Badge
                  key={cls}
                  variant="outline"
                  className={cn('text-[10px]', CLASSIFICATION_STYLES[cls] ?? 'bg-zinc-500/10')}
                >
                  {cls}
                </Badge>
              ))}
            </div>
          </section>
        )}

        {/* Stop Button Procedure */}
        <section>
          <SectionTitle icon={Pause} accent="red">Stop Button Procedure</SectionTitle>
          <div className="rounded-lg border border-red-500/20 bg-red-500/5 p-3">
            <p className="text-sm leading-relaxed">{system.stopButtonProcedure}</p>
            {system.humanOversightDocUrl && (
              <a
                href={system.humanOversightDocUrl}
                className="inline-flex items-center gap-1 text-xs text-primary hover:underline mt-2"
                onClick={(e) => e.preventDefault()}
              >
                <FileText className="h-3 w-3" />
                View human oversight procedure
              </a>
            )}
          </div>
        </section>

        {/* Dependency Graph */}
        <section>
          <SectionTitle icon={Network}>Dependency Graph</SectionTitle>
          <div className="rounded-lg border p-4 bg-muted/20">
            <div className="flex items-start gap-2 text-xs">
              {/* Upstream */}
              <div className="flex-1 min-w-0">
                <p className="text-[11px] uppercase tracking-wider text-muted-foreground mb-2">Upstream</p>
                {system.upstreamDependencies.length === 0 ? (
                  <p className="text-muted-foreground italic">None (root)</p>
                ) : (
                  <div className="space-y-1">
                    {system.upstreamDependencies.map((dep) => {
                      const upSys = AI_SYSTEMS.find(s => s.id === dep);
                      return (
                        <div key={dep} className="rounded-md bg-popover border px-2 py-1.5 text-[11px] truncate">
                          {upSys?.name ?? dep}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Arrow */}
              <div className="flex items-center pt-6">
                <ArrowRight className="h-4 w-4 text-muted-foreground" />
              </div>

              {/* This system */}
              <div className="flex-1 min-w-0">
                <p className="text-[11px] uppercase tracking-wider text-primary mb-2">This System</p>
                <div className={cn(
                  'rounded-md border-2 px-2 py-1.5 text-[11px] font-semibold truncate',
                  'border-primary bg-primary/5',
                )}>
                  {system.name}
                </div>
              </div>

              {/* Arrow */}
              <div className="flex items-center pt-6">
                <ArrowRight className="h-4 w-4 text-muted-foreground" />
              </div>

              {/* Downstream */}
              <div className="flex-1 min-w-0">
                <p className="text-[11px] uppercase tracking-wider text-muted-foreground mb-2">Downstream</p>
                {system.downstreamConsumers.length === 0 ? (
                  <p className="text-muted-foreground italic">None</p>
                ) : (
                  <div className="space-y-1">
                    {system.downstreamConsumers.map((dep) => {
                      const downSys = AI_SYSTEMS.find(s => s.id === dep);
                      return (
                        <div key={dep} className="rounded-md bg-popover border px-2 py-1.5 text-[11px] truncate">
                          {downSys?.name ?? dep}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>

        {/* Framework Relevance */}
        {system.frameworkRelevance.length > 0 && (
          <section>
            <SectionTitle icon={Shield}>Framework Relevance</SectionTitle>
            <div className="flex flex-wrap gap-1.5">
              {system.frameworkRelevance.map((fr) => (
                <Badge key={fr} variant="outline" className="text-[10px] bg-primary/5 text-primary border-primary/20">
                  {FRAMEWORK_LABELS[fr] ?? fr}
                </Badge>
              ))}
            </div>
          </section>
        )}

        {/* Compliance Status */}
        <section>
          <SectionTitle icon={CheckCircle2}>Compliance Status</SectionTitle>
          <div className="grid grid-cols-2 gap-2">
            <ComplianceStat
              label="Conformity Assessment"
              value={timeAgo(system.conformityAssessmentDate)}
              icon={CheckCircle2}
              stale={daysSince(system.conformityAssessmentDate) > 365}
            />
            <ComplianceStat
              label="Last Bias Test"
              value={timeAgo(system.lastBiasTestDate)}
              icon={FlaskConical}
              stale={daysSince(system.lastBiasTestDate) > 90}
            />
            <ComplianceStat
              label="Faithfulness Score"
              value={formatPercent(system.faithfulnessScore, 1)}
              icon={TrendingUp}
              valueColor={faithfulnessColor(system.faithfulnessScore)}
            />
            <ComplianceStat
              label="Hallucination (30d)"
              value={formatPercent(system.hallucinationRate30d, 2)}
              icon={TrendingDown}
              valueColor={hallucinationColor(system.hallucinationRate30d)}
            />
            <ComplianceStat
              label="Injection Block Rate"
              value={formatPercent(injectionRate, 1)}
              icon={Shield}
              valueColor={injectionRate >= 0.99 ? 'text-emerald-600 dark:text-emerald-400' : 'text-amber-600 dark:text-amber-400'}
            />
            <ComplianceStat
              label="Bias Test Pass Rate"
              value={formatPercent(system.biasTestPassRate, 0)}
              icon={Eye}
              valueColor={biasTestColor(system.biasTestPassRate)}
            />
          </div>
        </section>

        <Separator />

        {/* Actions */}
        <section>
          <SectionTitle icon={Zap}>Actions</SectionTitle>
          <div className="grid grid-cols-2 gap-2">
            <Button
              variant="outline"
              size="sm"
              className="justify-start gap-2 h-9"
              onClick={() => onRunBiasTest(system)}
            >
              <FlaskConical className="h-3.5 w-3.5" />
              Run Bias Test
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="justify-start gap-2 h-9 text-amber-600 hover:text-amber-600 dark:text-amber-400 dark:hover:text-amber-400"
              onClick={() => onPauseAgent(system)}
              disabled={system.status !== 'in_production'}
            >
              {system.status === 'in_production' ? (
                <><Pause className="h-3.5 w-3.5" /> Pause Agent</>
              ) : (
                <><Play className="h-3.5 w-3.5" /> Not Active</>
              )}
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="justify-start gap-2 h-9"
              onClick={() => onViewAudit(system)}
            >
              <History className="h-3.5 w-3.5" />
              Audit History
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="justify-start gap-2 h-9"
              onClick={() => onGenerateReport(system)}
            >
              <FileText className="h-3.5 w-3.5" />
              Compliance Report
            </Button>
            {system.linkedAgentId && (
              <Button
                size="sm"
                className="justify-start gap-2 h-9 col-span-2"
                onClick={() => onOpenInAgents(system)}
              >
                <ExternalLink className="h-3.5 w-3.5" />
                Open in Agents module
              </Button>
            )}
          </div>
        </section>
      </div>
    </>
  );
}

// ── Small Components ────────────────────────────────────────────────────────

function SectionTitle({
  icon: Icon, children, accent = 'default',
}: {
  icon: typeof Bot;
  children: React.ReactNode;
  accent?: 'default' | 'red';
}) {
  return (
    <h3 className={cn(
      'flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider mb-2',
      accent === 'red' ? 'text-red-600 dark:text-red-400' : 'text-foreground',
    )}>
      <Icon className="h-3.5 w-3.5" />
      {children}
    </h3>
  );
}

function ComplianceStat({
  label, value, icon: Icon, stale = false, valueColor,
}: {
  label: string;
  value: string;
  icon: typeof Bot;
  stale?: boolean;
  valueColor?: string;
}) {
  return (
    <div className={cn(
      'rounded-lg border p-2.5',
      stale && 'border-red-500/30 bg-red-500/5',
    )}>
      <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-muted-foreground">
        <Icon className="h-3 w-3" />
        {label}
      </div>
      <p className={cn(
        'text-sm font-semibold mt-0.5 tabular-nums',
        valueColor,
        stale && !valueColor && 'text-red-600 dark:text-red-400',
      )}>
        {value}
      </p>
    </div>
  );
}
